import { ConnectedPosition, FlexibleConnectedPositionStrategy, Overlay, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Directive, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { AwesomeTooltipV2Component } from './awesome-tooltip-v2.component';
import { NavigationEnd, Router } from '@angular/router';
import { filter, take, tap } from 'rxjs/operators';

export type PositionTooltipType = 'topCenter' | 'topRight' | 'topLeft' | 'bottomCenter' | 'rightCenter' | 'leftCenter';

@Directive({
  selector: '[appAwesomeTooltipV2]',
  standalone: false,
})
export class AwesomeTooltipV2Directive implements OnInit, OnDestroy {
  @Input() positionTooltip: PositionTooltipType = 'topCenter';
  @Input() patientSignerSaved = null;
  @Input() textAlign: 'center' | 'left' = 'center';
  @Input() tooltipText: string = '';
  @Input() actions: boolean = false;
  @Input() insideTable: boolean = false;
  @Input() isEnableTouch: boolean = true;

  private overlayRef: OverlayRef;
  private touched: boolean = false;
  positionsObj: ConnectedPosition;

  constructor(
    private overlayPositionBuilder: OverlayPositionBuilder,
    private elementRef: ElementRef,
    private overlay: Overlay,
    private router: Router,
  ) {}

  ngOnInit() {
    window.dispatchEvent(new Event('scroll', { bubbles: true, cancelable: true }));
    window.addEventListener('scroll', this.hide.bind(this), true);
    document.addEventListener('touchstart', this.onDocumentTouch.bind(this), true);
    this.setPositions();

    const positionStrategy: FlexibleConnectedPositionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions([this.positionsObj]);

    this.overlayRef = this.overlay.create({ positionStrategy });
    this.router.events
      .pipe(
        filter((event): boolean => event instanceof NavigationEnd),
        tap(() => this.hide()),
        take(1),
      )
      .subscribe();
  }

  ngOnDestroy() {
    if (this.isEnableTouch && this.touched) this.overlayRef?.detach();
  }

  private setPositions(): void {
    if (this.positionTooltip === 'topCenter') {
      this.positionsObj = {
        originX: 'center',
        originY: 'top',
        overlayX: 'center',
        overlayY: 'bottom',
      };
    }
    if (this.positionTooltip === 'topRight') {
      this.positionsObj = {
        originX: 'end',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'bottom',
      };
    }
    if (this.positionTooltip === 'topLeft') {
      this.positionsObj = {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
      };
    }
    if (this.positionTooltip === 'bottomCenter') {
      this.positionsObj = {
        originX: 'center',
        originY: 'bottom',
        overlayX: 'center',
        overlayY: 'top',
      };
    }
    if (this.positionTooltip === 'rightCenter') {
      this.positionsObj = {
        originX: 'end',
        originY: 'center',
        overlayX: 'start',
        overlayY: 'center',
      };
    }
    if (this.positionTooltip === 'leftCenter') {
      this.positionsObj = {
        originX: 'start',
        originY: 'center',
        overlayX: 'end',
        overlayY: 'center',
      };
    }
  }

  @HostListener('touchstart', ['$event'])
  onMobileTouch(): void {
    if (this.isEnableTouch) {
      this.touched = true;
      this.toggleTooltip();
    }
  }

  @HostListener('mouseenter')
  show(): void {
    if (!this.touched) {
      this.attachTooltip();
    }
  }

  @HostListener('mouseleave')
  hide(): void {
    if (!this.touched) {
      this.overlayRef?.detach();
    }
  }

  @HostListener('click', ['$event'])
  onClick(): void {
    this.hide();
  }

  private toggleTooltip(): void {
    if (this.overlayRef?.hasAttached()) {
      this.hide();
    } else {
      this.attachTooltip();
    }
  }

  private attachTooltip(): void {
    const component = AwesomeTooltipV2Component;
    const tooltipPortal = new ComponentPortal(component);
    const tooltipRef = this.overlayRef?.attach(tooltipPortal);
    tooltipRef.instance.positionTooltip = this.positionTooltip;
    tooltipRef.instance.patientSignerSaved = this.patientSignerSaved;
    tooltipRef.instance.textAlign = this.textAlign;
    tooltipRef.instance.actions = this.actions;
    tooltipRef.instance.insideTable = this.insideTable;
    tooltipRef.instance.tooltipText = this.tooltipText;
  }

  private onDocumentTouch(event: TouchEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.overlayRef?.detach();
      if (this.isEnableTouch) {
        this.touched = false;
      }
    }
  }
}
